import React from 'react';

// Пошаговое описание алгоритма расчёта премии mercantilebrace (X-Wind Commercial Property)
// + перекрёстные ссылки на таблицы Rate Tables + «Worked example» с реальными числами
// по введённым данным. Текст интерфейса — только английский (правило CLAUDE.md).
// Формулы отражают models/mercantilebrace__1_0_0.cfc.

const boxStyle = {
  fontFamily: 'monospace',
  fontSize: '1em',
  background: '#f8f9fa',
  border: '1px solid #e9ecef',
  borderRadius: '4px',
  padding: '8px 12px',
  margin: '6px 0',
  whiteSpace: 'pre-wrap',
  overflowX: 'auto'
};

const money = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');

// Название таблицы по её якорю (для чипов-ссылок)
const SECTION_NAME = {
  NonWindBaseRate: 'Non-Wind Base Rate',
  protectionClass: 'Protection Class / Cause of Loss',
  FireMod: 'Fire Mod',
  PropDeductible: 'AOP Deductible / Named Storm',
  AgeOfBuildingCredits: 'Age of Building',
  PrivateProtectionsCredits: 'Private Protections & Risk',
  BuildingVacancy: 'Building Vacancy',
  WindBaseRate: 'Wind Base Rate',
  WindHailDeductible: 'Wind Deductible',
  ShuttersCredit: 'Shutters',
  Roofreplaced: 'Roof Replaced',
  lineTRIA: 'TRIA'
};

// Разбор hint на группы (аналог parseHint на странице)
const parseHint = (hint) => {
  const g = { info: [], nonWind: [], wind: [], footer: [] };
  if (!Array.isArray(hint)) return g;
  let cur = 'info';
  for (const it of hint) {
    if (it.center === 'Non-Wind Coverage') { cur = 'nonWind'; continue; }
    if (it.center === 'Wind Coverage') { cur = 'wind'; continue; }
    if (it.break) { cur = 'footer'; continue; }
    if (it.center) continue;
    g[cur].push(it);
  }
  return g;
};
const hv = (it) => {
  const v = it?.valueDisplay?.value !== undefined ? it.valueDisplay.value : it?.value;
  if (v === undefined || v === null) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
};

const Step = ({ n, title, children }) => (
  <div style={{ marginBottom: '14px' }}>
    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
      <span style={{ color: '#0d6efd' }}>Step {n}.</span> {title}
    </div>
    {children}
  </div>
);
const Formula = ({ children }) => <div style={boxStyle}>{children}</div>;
const Note = ({ children }) => (
  <div style={{ fontSize: '0.85em', color: '#666', margin: '4px 0' }}>{children}</div>
);

// Ряд чипов-ссылок на таблицы Rate Tables
const TableRefs = ({ sections, onOpen }) => {
  if (!onOpen || !sections || !sections.length) return null;
  return (
    <div style={{ margin: '4px 0 2px', fontSize: '0.82em' }}>
      <span style={{ color: '#888' }}>Rate tables: </span>
      {sections.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onOpen(s)}
          title={`Open the ${SECTION_NAME[s] || s} rate table`}
          style={{
            margin: '2px 4px 2px 0',
            padding: '1px 8px',
            borderRadius: '10px',
            border: '1px solid #0d6efd',
            background: '#e7f1ff',
            color: '#0d6efd',
            fontSize: '0.92em',
            cursor: 'pointer'
          }}
        >
          {SECTION_NAME[s] || s} ?
        </button>
      ))}
    </div>
  );
};

// Формула-строка члена: приглушённая подпись + жирное значение
const term = (label, value, key) => (
  <span key={key} style={{ whiteSpace: 'nowrap', marginRight: '6px' }}>
    <span style={{ color: '#6b7280', marginRight: '4px' }}>{label}</span>
    <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>{value}</span>
  </span>
);

// --- Worked example: трасса одного покрытия по реальным числам ---
const renderCoverageTrace = (cov, risk, keyId) => {
  const r = cov._rated;
  if (!r) return null;
  const g = parseHint(r.hint);
  const hasWind = g.wind.length > 0 || parseFloat(r.premiumWind) > 0;

  const productLine = (group) => {
    if (!group.length) return null;
    const finalRow = group.find((x) => /^Final/i.test(x.title || '')) || group[group.length - 1];
    const factors = group.filter((x) => x !== finalRow);
    return (
      <div style={{ ...boxStyle, whiteSpace: 'normal' }}>
        {factors.map((it, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ color: '#adb5bd', margin: '0 3px' }}>×</span>}
            {term(it.title, hv(it), `f${i}`)}
          </React.Fragment>
        ))}
        <span style={{ color: '#adb5bd', margin: '0 3px' }}>=</span>
        {term(finalRow.title, hv(finalRow), 'fin')}
        <span style={{ color: '#8a94a6', fontSize: '0.78em', marginLeft: '4px' }}>(per $100)</span>
      </div>
    );
  };

  return (
    <div key={keyId} style={{ marginBottom: '14px', paddingLeft: '10px', borderLeft: '3px solid #b3d7ff' }}>
      <div style={{ fontWeight: 600, marginBottom: '2px' }}>
        Location {risk.extraData?.locationId} / Building {risk.extraData?.buildingId} — {cov.type}
        <span style={{ color: '#888', fontWeight: 400 }}> (limit {money(cov.limit)})</span>
      </div>

      <div style={{ fontSize: '0.85em', color: '#0d6efd', marginTop: '4px' }}>Steps 1–2 · Non-Wind rate</div>
      {productLine(g.nonWind)}

      {hasWind && (
        <>
          <div style={{ fontSize: '0.85em', color: '#0d6efd', marginTop: '4px' }}>Steps 3–4 · Wind rate</div>
          {productLine(g.wind)}
        </>
      )}

      <div style={{ fontSize: '0.85em', color: '#0d6efd', marginTop: '4px' }}>Step 5 · Combined rate → premium</div>
      <Formula>{`${r.finalNonWindRate ?? ''}${hasWind ? ` + ${r.finalWHRateFinal ?? r.premiumWind_Hint ?? ''}` : ''} = ${r.finalRate} (per $100)
${r.finalRate} / 100 × ${money(cov.limit)} = ${money(r.premium)}`}</Formula>
    </div>
  );
};

const renderWorkedExample = (result, onOpen) => {
  const lp = result?.policy?.lineProperty;
  const rated = result?.policy?._rated;
  if (!lp?._rated || !rated) return null;

  const risks = lp.risk || {};
  const propertyPremium = lp._rated.premium;
  const premiumMinimum = parseFloat(lp._rated.premiumMinimum) || 0;
  const premiumCoDiscount = parseFloat(lp._rated.premiumCoDiscount) || 0;
  const tria = result?.policy?.lineTRIA?._rated;
  const triaPremium = tria?.premium;
  const totalPremium = rated.premium;
  const totalFee = rated.fee;
  const grandTotal = (parseFloat(rated.premium) || 0) + (parseFloat(rated.fee) || 0);

  const covSum = Object.keys(risks).reduce((s, rk) => {
    const cs = risks[rk]?.coverages || {};
    return s + Object.keys(cs).reduce((a, ck) => a + (parseFloat(cs[ck]?._rated?.premium) || 0), 0);
  }, 0);

  return (
    <div style={{ marginTop: '18px', borderTop: '2px solid #b3d7ff', paddingTop: '12px' }}>
      <h4 style={{ margin: '0 0 8px' }}>Worked example — your data</h4>
      <Note>Actual factor values and amounts from the latest rating of the data currently entered.</Note>

      <div style={{ fontWeight: 'bold', margin: '8px 0 4px' }}>A. Per building coverage</div>
      {Object.keys(risks).map((rk) => {
        const risk = risks[rk];
        if (!risk?.coverages) return null;
        return Object.keys(risk.coverages).map((ck) =>
          renderCoverageTrace(risk.coverages[ck], risk, rk + ck)
        );
      })}

      <div style={{ fontWeight: 'bold', margin: '10px 0 4px' }}>B. Per policy</div>

      <Step n="6" title="Property premium (sum + minimum)">
        <Formula>{`Σ coverage premiums = ${money(covSum)}${premiumMinimum > 0 ? `\n+ minimum property premium adjustment = ${money(premiumMinimum)}` : ''}
= Property premium ${money(propertyPremium)}`}</Formula>
      </Step>

      {triaPremium !== undefined && (
        <Step n="7" title="TRIA (Terrorism)">
          <Formula>{Array.isArray(tria?.HintRates)
            ? tria.HintRates.map((h) => `${String(h.title).replace(/<[^>]+>/g, '')} : ${String(h.value).trim()}`).join('\n')
            : `TRIA premium = ${money(triaPremium)}`}</Formula>
          <TableRefs sections={['lineTRIA']} onOpen={onOpen} />
        </Step>
      )}

      {premiumCoDiscount !== 0 && (
        <Step n="8" title="Final premium credit">
          <Formula>{`Final premium credit adjustment = ${money(premiumCoDiscount)}
Property premium after credit = ${money(propertyPremium)}`}</Formula>
        </Step>
      )}

      <Step n="9" title="Grand total">
        <Formula>{`Total Premium = ${money(totalPremium)}${totalFee !== undefined ? `\n+ Fees = ${money(totalFee)}` : ''}
= Grand Total ${money(grandTotal)}`}</Formula>
      </Step>
    </div>
  );
};

const MercantileBraceAlgorithm = ({ result, onOpenTable }) => (
  <div style={{ fontSize: '1em', lineHeight: 1.5 }}>
    <p style={{ color: '#555', marginTop: 0 }}>
      The premium is built <b>per building coverage</b> (Non-Wind rate + Wind rate), converted to a
      coverage premium, then all coverages are aggregated to the policy and TRIA / final adjustments
      are applied. Each factor links to its Rate Table (the “?” chips).
    </p>

    <h4 style={{ margin: '10px 0 6px' }}>A. Per building coverage</h4>

    <Step n="1" title="Non-Wind base rate">
      <Formula>{`base = ceil( NonWindBaseRate × ProtectionClass × 1000 ) / 1000`}</Formula>
      <Note>
        <b>NonWindBaseRate</b> — by [occupancy][construction]; <b>ProtectionClass</b> — factor by PC. Rounded up to 3 decimals.
      </Note>
      <TableRefs sections={['NonWindBaseRate', 'protectionClass']} onOpen={onOpenTable} />
    </Step>

    <Step n="2" title="Non-Wind rate (apply credits / debits)">
      <Formula>{`nonWindRate = round2( base
    × FireMod × AopDedCredit × CauseOfLossFactor × BuildingAgeCredit
    × AlarmOrSecurity × FullySprinklered × FullyUpdated10Yrs
    × HighCrimeDebit × LossFreeCredit × ManualDbCr(Non-Wind) × BuildingVacancy )`}</Formula>
      <Note>All factors default to 1 (no effect) when not applicable. Result is per $100 of limit.</Note>
      <TableRefs
        sections={['FireMod', 'PropDeductible', 'protectionClass', 'AgeOfBuildingCredits', 'PrivateProtectionsCredits', 'BuildingVacancy']}
        onOpen={onOpenTable}
      />
    </Step>

    <Step n="3" title="Wind base rate">
      <Formula>{`windBase = IsBackYard ? RateBackYard : Rate      (by tier / construction / distance)

if ( Zone 1  AND  yearBuilt < 1996  AND  not Tri-County ):
    windBase = windBase × BuildingAgeWindSurcharge`}</Formula>
      <Note>The wind tier and zone come from the County table (via the selected county).</Note>
      <TableRefs sections={['WindBaseRate']} onOpen={onOpenTable} />
    </Step>

    <Step n="4" title="Wind rate (apply credits / debits)">
      <Formula>{`windRate = round2( windBase
    × CauseOfLossFactor × ManualDbCr(Wind) × WindHailDeductible × ShuttersCredit
    × TriCountyDiscount × RoofReplaced × NamedStormDebit × MarketDebit )

if ( Wind not included ):  windRate = 0`}</Formula>
      <TableRefs
        sections={['protectionClass', 'WindHailDeductible', 'ShuttersCredit', 'Roofreplaced', 'PropDeductible']}
        onOpen={onOpenTable}
      />
    </Step>

    <Step n="5" title="Combined rate → coverage premium">
      <Formula>{`combinedRate  = nonWindRate + windRate           (per $100 of limit)
coveragePremium = round( combinedRate / 100 × Limit )

if ( coveragePremium < coverage minimum ):  coveragePremium = coverage minimum
if ( RateAdjustment ≠ 1 ):                  coveragePremium = round( coveragePremium × RateAdjustment )`}</Formula>
    </Step>

    <h4 style={{ margin: '14px 0 6px' }}>B. Per policy</h4>

    <Step n="6" title="Property premium (sum + minimum)">
      <Formula>{`propertyPremium = Σ coveragePremium        (all buildings / coverages)

if ( propertyPremium < MinimumPropertyPremium ):
    propertyPremium = MinimumPropertyPremium`}</Formula>
      <Note>Minimum property premium comes from <b>MinimumPropertyPremium</b> (Alone vs WithGL).</Note>
    </Step>

    <Step n="7" title="TRIA (Terrorism) — only if selected">
      <Formula>{`per location:
    byTIV      = TIV(location limit) × TIV_Multiplier
    byPremium  = location property premium × AP_Multiplier
    triaLoc    = max( byTIV , byPremium )        (metropolitan county → higher multipliers)

triaPremium = round( Σ triaLoc )
if ( triaPremium < MinPr ):  triaPremium = MinPr      (MinPr = USD 50)`}</Formula>
      <TableRefs sections={['lineTRIA']} onOpen={onOpenTable} />
    </Step>

    <Step n="8" title="Final premium credit (optional)">
      <Formula>{`if ( FinalPremiumCredit ≠ 1 ):
    credit = -( propertyPremium + triaPropertyPremium ) × (1 − FinalPremiumCredit)
    propertyPremium = propertyPremium + credit`}</Formula>
      <Note>e.g. FinalPremiumCredit = 0.9 → a 10% credit on the property (+ TRIA property) premium.</Note>
    </Step>

    <Step n="9" title="Grand total">
      <Formula>{`Grand Total = propertyPremium + triaPremium + policy fees / taxes`}</Formula>
    </Step>

    <p style={{ fontSize: '0.85em', color: '#888', marginTop: '10px' }}>
      round2 = round to 2 decimals; round = round to the nearest whole dollar; ceil = round up.
    </p>

    {result && renderWorkedExample(result, onOpenTable)}
  </div>
);

export default MercantileBraceAlgorithm;
